import store from ".";

const apiBaseURL = process.env.VUE_APP_API_BASE_URL;


export const getters = {
    applications: (state) => state.applications,
    authorizedReps: (state) => state.authorized_reps,
    properties: (state) => state.properties,
    payments: (state) => state.payments,
    propertyOwners: (state) => state.propertyOwners,
    selectedPropertyOwner: (state) => state.selectedPropertyOwner,
    arAllApplications: (state) => state.arAllApplications,
    loadingApplications: (state) => state.loadingApplications,
    loadingProperties: (state) => state.loadingProperties,
    loadingPayments: (state) => state.loadingPayments,
    // Filter to only show property owners are active for
    activePropertyOwners: (state) => state.propertyOwners.filter(po => po.status === 'Active'),
}

export const state = () => ({
  applications: [],
  authorized_reps: [],
  properties: [],
  payments: [],
  propertyOwners: [],
  selectedPropertyOwner: [],
  arAllApplications: [], // All PMR applications for the AR (fetched once, cached)
  loadingApplications: false,
  loadingProperties: false,
  loadingPayments: false,
});

export const mutations = {
  setApplications(state,applications){
    state.applications = applications;
  },
  setAuthorizedReps(state,authorized_reps){
    state.authorized_reps = authorized_reps;
  },
  setProperties(state,properties){
    state.properties = properties; 
  },
  setPayments(state,payments){
    state.payments = payments;
  },
  addPayment(state,payment){
    state.payments.push(payment);
  },
  setPrimaryPayment(state, paymentKey) {
    const oldPrimary = state.payments.find(p => p.isPrimary);
    const newPrimary = state.payments.find(p => p.rid == paymentKey || p.id == paymentKey);
    if (oldPrimary){
      oldPrimary.isPrimary = false;
      oldPrimary.is_modified = true;
    }

    if (newPrimary){
      newPrimary.isPrimary = true;
      newPrimary.is_modified = true;
    }

  },
  updatePaymentStatus(state, { rid, status }) {
    const payment = state.payments.find(p => p.rid === rid);
    if (payment) {
      payment.bank_info_status = status;
    }
  },
  setPropertyOwners(state, propertyOwners) {
    state.propertyOwners = propertyOwners;
  },
  setSelectedPropertyOwner(state, selectedPropertyOwner){
    state.selectedPropertyOwner = selectedPropertyOwner;
  },
  setArAllApplications(state, applications) {
    state.arAllApplications = applications;
  },
  setLoadingApplications(state, loading) {
    state.loadingApplications = loading;
  },
  setLoadingProperties(state, loading) {
    state.loadingProperties = loading;
  },
  setLoadingPayments(state, loading) {
    state.loadingPayments = loading;
  },
};

export const actions = {
  async fetchApplications(context){
    try {
      const user = context.rootGetters['auth/userInfo'];
        
      const response = await fetch(`${apiBaseURL}/applications/${user.qbUser.rid}`);
      if (response.ok){
        const data = await response.json();
        context.commit('setApplications', data.applications || []);
        return { success: true, applications: data.applications };

      } 
      context.commit('setApplications', []);
      return { success: false, applications: []};
        
    } catch (error){
      console.error("Error fetching applications:", error);
      context.commit('setApplications', []);
      return { success: false, error };
    }
  },
  async fetchAuthorizedReps(context, related_organization){
    try {
      const response = await fetch(`${apiBaseURL}/user/authorized-reps/${related_organization}`);
      if (response.ok){
        const data = await response.json();
        context.commit('setAuthorizedReps', data.authorized_reps || []);
        return { success: true, authorized_reps: data.authorized_reps };
      } 
      context.commit('setAuthorizedReps', []);
      return { success: false, authorized_reps: []};
        
    } catch (error){
      console.error("Error fetching authorized reps:", error);
      context.commit('setAuthorizedReps', []);
      return { success: false, error };
    }
  },
  async fetchProperties(context, options = {}){
    context.commit('setLoadingProperties', true);
    try {
      const user = context.rootGetters['auth/userInfo'];
      const { organization_rid, ar_email } = options;
      // Use provided organization_rid or fall back to user's organization
      const orgRid = organization_rid || user?.qbUser?.related_organization;

      if (!orgRid) {
        console.error("No organization RID found");
        context.commit('setProperties', []);
        return { success: false, properties: []};
      }

      // Build URL with optional ar_email query param
      let url = `${apiBaseURL}/properties/${orgRid}`;
      if (ar_email) {
        url += `?ar_email=${encodeURIComponent(ar_email)}`;
      }

      const response = await fetch(url);
      if (response.ok){
        const data = await response.json();
        context.commit('setProperties', data.properties || []);
        return { success: true, properties: data.properties || []};
      }
      context.commit('setProperties', []);
      return { success: false, properties: []};

    } catch (error){
      console.error("Error fetching properties:", error);
      context.commit('setProperties', []);
      return { success: false, error };
    } finally {
      context.commit('setLoadingProperties', false);
    }
  },
  async fetchPropertyOwners(context) {
    try {
      const user = context.rootGetters['auth/userInfo'];
      const ar_sso_id = user?.qbUser?.sso_id;

      if (!ar_sso_id) {
        console.error("No SSO ID found for user");
        context.commit('setPropertyOwners', []);
        return { success: false, propertyOwners: []};
      }

      const response = await fetch(`${apiBaseURL}/user/property-owners/${ar_sso_id}`);
      if (response.ok) {
        const data = await response.json();
        context.commit('setPropertyOwners', data.property_owners || []);
        return { success: true, propertyOwners: data.property_owners || []};
      } 
      context.commit('setPropertyOwners', []);
      return { success: false, propertyOwners: []};
      
    } catch (error) {
      console.error("Error fetching property owners:", error);
      context.commit('setPropertyOwners', []);
      return { success: false, error };
    }
  },
  async fetchPayments(context, organization_rid = null){
    context.commit('setLoadingPayments', true);
    try{
      const user = context.rootGetters['auth/userInfo'];
      // Use provided organization_rid or fall back to user's organization
      const orgRid = organization_rid || user.qbUser.related_organization;
      const response = await fetch(`${apiBaseURL}/payments/${orgRid}`);
      if (response.ok){
        const data = await response.json();
        context.commit('setPayments', data.payments);
        console.log("RESPONSE FROM PAYMENTS", data);
        return { success: true, payments: data.payments };
      }
      return { success: false, payments: []};

    } catch (error){
      console.error("Error fetching payments:", error);
      context.commit('setPayments', []);
      return { success: false, error };
    } finally {
      context.commit('setLoadingPayments', false);
    }
  },
  async submitPayment(context, { organization_rid, paymentData }) {
    try {
      const response = await fetch(`${apiBaseURL}/payments/${organization_rid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          message: data.error ? `[${data.error.code}] ${data.error.message}` : `Error ${response.status}: An unexpected error occurred. Please try again.`,
          status: response.status,
        };
      }

      const data = await response.json();
      context.commit('addPayment', data.payment);
      return { success: true, payment: data.payment };

    } catch (error) {
      console.error('Error submitting payment:', error);
      return {
        success: false,
        message: 'Network error: Unable to connect to the server. Please check your connection and try again.',
        error,
      };
    }
  },
  async uploadFileForPayment(context, { bankInfoRid, file, documentType }) {
    try {
      const response = await fetch(`${apiBaseURL}/payments/${bankInfoRid}/files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentType: documentType,
          filename: file.name,
          base64Data: file.base64Data,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          message: data.error ? `[${data.error.code}] ${data.error.message}` : `Error ${response.status}: Failed to upload file`,
          status: response.status,
        };
      }

      const data = await response.json();
      return { success: true, file: data.file };

    } catch (error) {
      console.error('Error uploading file:', error);
      return {
        success: false,
        message: 'Network error: Unable to upload file. Please check your connection and try again.',
        error,
      };
    }
  },
  async submitProperty(context, { organization_rid, propertyData }) {
    try {
      const response = await fetch(`${apiBaseURL}/properties/${organization_rid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(propertyData),
      });

      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          message: data.error ? `[${data.error.code}] ${data.error.message}` : `Error ${response.status}: An unexpected error occurred. Please try again.`,
          status: response.status,
        };
      }

      const data = await response.json();
      return { success: true, property: data.property };

    } catch (error) {
      console.error('Error submitting property:', error);
      return {
        success: false,
        message: 'Network error: Unable to connect to the server. Please check your connection and try again.',
        error,
      };
    }
  },
  async uploadFileForProperty(context, { propertyRid, file, documentType }) {
    try {
      const user = context.rootGetters['auth/userInfo'];
      const organization_rid = user?.qbUser?.related_organization;

      if (!organization_rid) {
        return {
          success: false,
          message: 'Unable to determine organization',
        };
      }

      const response = await fetch(`${apiBaseURL}/properties/${organization_rid}/${propertyRid}/files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentType: documentType,
          filename: file.name,
          base64Data: file.base64Data,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          message: data.error ? `[${data.error.code}] ${data.error.message}` : `Error ${response.status}: Failed to upload file`,
          status: response.status,
        };
      }

      const data = await response.json();
      return { success: true, file: data.file };

    } catch (error) {
      console.error('Error uploading file for property:', error);
      return {
        success: false,
        message: 'Network error: Unable to upload file. Please check your connection and try again.',
        error,
      };
    }
  },
  savePayments(context){
    try {
      const modifiedPayments = context.state.payments.filter(payment => payment.is_modified);
      //TO DO:make api call to backend to save new/modified payments here
    } catch (error){
      console.error("Error saving payments:", error);
      return { success: false, error };

    }
  },

  async fetchAllData(context){
    // Get user from auth store
    const user = context.rootGetters['auth/userInfo'];
    if (!user || !user.qbUser){
      console.error("No logged in user. Returning...");
      return [];
    }

    // AR users: only fetch property owners initially
    // Properties/payments are loaded after PO selection in handlePropertyOwnerSelect
    if (user.qbUser.role === 'ar') {
      const results = await Promise.all([
        context.dispatch('fetchPropertyOwners'),
      ]);
      console.log("AR RESULTS", results);
      return results;
    }

    // Property Owners: fetch all data
    const results = await Promise.all([
      context.dispatch('fetchApplications'),
      context.dispatch('fetchAuthorizedReps', user.qbUser.related_organization),
      context.dispatch('fetchProperties'),
      context.dispatch('fetchPayments'),
    ]);
    console.log("RESULTS", results);

    return results;
  },

  // Fetch applications by organization RID (used for AR filtering)
  async fetchApplicationsByOrganization(context, organization_rid) {
    try {
      const response = await fetch(`${apiBaseURL}/applications/organization/${organization_rid}`);
      if (response.ok) {
        const data = await response.json();
        return { success: true, applications: data.applications || [] };
      }
      return { success: false, applications: [] };
    } catch (error) {
      console.error("Error fetching applications by organization:", error);
      return { success: false, error };
    }
  },

  // Fetch and filter AR applications for the selected property owner
  async fetchARApplicationsForSelectedOwner(context, organization_rid) {
    context.commit('setLoadingApplications', true);

    try {
      const user = context.rootGetters['auth/userInfo'];

      // Step 1: Get AR's PMR applications (cached)
      if (context.state.arAllApplications.length === 0) {
        const arAppsResponse = await fetch(`${apiBaseURL}/applications/${user.qbUser.rid}`);
        if (arAppsResponse.ok) {
          const data = await arAppsResponse.json();
          context.commit('setArAllApplications', data.applications || []);
        }
      }

      // Step 2: Get Property Owner applications for selected org
      const orgAppsResult = await context.dispatch('fetchApplicationsByOrganization', organization_rid);

      // Step 3: Extract EDP numbers from property owner applications
      const poEdpNumbers = new Set(
        orgAppsResult.applications.map(app => app.edp_number).filter(Boolean)
      );

      // Step 4: Filter AR applications by matching EDP numbers
      const filteredApplications = context.state.arAllApplications.filter(
        app => poEdpNumbers.has(app.edp_number)
      );

      // Remove duplicates by EDP number
      const uniqueApplications = [...new Map(
        filteredApplications.map(app => [app.edp_number, app])
      ).values()];

      context.commit('setApplications', uniqueApplications);
      return { success: true, applications: uniqueApplications };
    } finally {
      context.commit('setLoadingApplications', false);
    }
  },
};

/* Logic for populating applications for ARs to make sure ARs only see Applications for property owners they are associated with. 
1. Query People-App table for all records where type = PMR AND related person (FID 7) equals to logged in AR's record ID (only once)
2. AR selects property owner to represent (organization RID)
3. Query People-App table for all records where type = "Property Owner" and related organization = selected organization rid
4. Filter 1. so we are only showing records with EDP# that are in 3. (remove duplicates to be safe)
5. AR selects another property owner repeat steps 2-4 
*/


export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};